import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ShieldCheck, KeyRound, Save, Loader2, CheckCircle2, Handshake, ArrowRight } from 'lucide-react';
import KakoAuthModal from '../components/KakoAuthModal';
import KakoIdentity from '../components/KakoIdentity';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatError = (detail) => {
  if (!detail) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return String(detail);
};

const ResellerLogin = () => {
  const [kakoId, setKakoId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // esqueci minha senha
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotId, setForgotId] = useState('');
  const [forgotToken, setForgotToken] = useState('');
  const [forgotModal, setForgotModal] = useState(false);
  const [forgotPass, setForgotPass] = useState('');
  const [forgotDone, setForgotDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/reseller/login`, {
        kako_id: kakoId,
        password: password
      });

      const { access_token, refresh_token } = response.data;
      localStorage.setItem('reseller_token', access_token);
      localStorage.setItem('reseller_refresh', refresh_token);

      navigate('/reseller/dashboard');
    } catch (error) {
      setError(formatError(error.response?.data?.detail) || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const doReset = async () => {
    setLoading(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/reseller/reset-password`, {
        kako_id: forgotId.trim(),
        verification_token: forgotToken,
        new_password: forgotPass,
      });
      setForgotDone(true);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const exitForgot = () => {
    setForgotMode(false); setForgotDone(false); setForgotToken('');
    setForgotPass(''); setForgotId(''); setError('');
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#150b2b] flex items-center justify-center p-4"
         data-testid="reseller-login-page">
      {/* brilhos de fundo */}
      <div className="pointer-events-none absolute -top-28 -left-24 h-80 w-80 rounded-full bg-purple-600/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -right-20 h-96 w-96 rounded-full bg-blue-500/25 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
           style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
                    backgroundSize: '22px 22px' }} />

      <div className="relative w-full max-w-md">
        <div className="mb-5 text-center">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl
                          bg-gradient-to-br from-purple-500 to-blue-600 shadow-xl shadow-purple-900/50
                          ring-1 ring-white/25">
            <Handshake className="h-8 w-8 text-white" strokeWidth={2.2} />
          </div>
          <h1 className="text-3xl font-black tracking-tight text-white">Painel do Afiliado</h1>
          <p className="mt-1 text-sm text-purple-200/80">
            FOX SELLER · acompanhe suas vendas e comissões
          </p>
        </div>

        <Card className="border-white/15 bg-white/95 shadow-2xl shadow-black/40 backdrop-blur">
          <CardContent className="pt-6">
            {forgotMode ? (
              <div className="space-y-3" data-testid="reseller-forgot-form">
                <p className="font-medium text-sm flex items-center gap-1.5">
                  <KeyRound className="w-4 h-4 text-purple-600" /> Recuperar senha de afiliado
                </p>
                {forgotDone ? (
                  <div className="text-center space-y-3 py-4">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                    <p className="text-sm">Senha alterada com sucesso! Faça login com a nova senha.</p>
                    <Button className="bg-gradient-to-r from-purple-600 to-blue-600"
                      data-testid="reseller-forgot-back-login" onClick={exitForgot}>
                      Ir para o login
                    </Button>
                  </div>
                ) : !forgotToken ? (
                  <>
                    <p className="text-xs text-gray-600">
                      Confirme que a conta KAKO é sua: enviaremos um código de 6 dígitos
                      no chat do app.
                    </p>
                    <Input data-testid="reseller-forgot-id-input" value={forgotId} inputMode="numeric"
                      maxLength={8}
                      onChange={(e) => setForgotId(e.target.value.replace(/\D/g, ''))}
                      placeholder="Seu KAKO ID de afiliado" />
                    <KakoIdentity kakoId={forgotId} tone="purple" testId="reseller-forgot-identity" />
                    {error && (
                      <Alert variant="destructive" data-testid="reseller-forgot-error">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
                      disabled={!forgotId || loading}
                      data-testid="reseller-forgot-verify-button" onClick={() => setForgotModal(true)}>
                      <ShieldCheck className="w-4 h-4 mr-2" /> Verificar no chat do app
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
                      ✅ Conta verificada! Agora defina sua nova senha.
                    </p>
                    <Input data-testid="reseller-forgot-new-password" type="password" value={forgotPass}
                      onChange={(e) => setForgotPass(e.target.value)}
                      placeholder="Nova senha (mín. 8 caracteres)" />
                    {error && (
                      <Alert variant="destructive" data-testid="reseller-forgot-error">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    <Button className="w-full bg-gradient-to-r from-purple-600 to-blue-600"
                      disabled={forgotPass.length < 8 || loading}
                      data-testid="reseller-forgot-submit-button" onClick={doReset}>
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Salvar nova senha
                    </Button>
                  </>
                )}
                {!forgotDone && (
                  <button className="text-xs text-gray-500 underline w-full text-center"
                    data-testid="reseller-forgot-cancel" onClick={exitForgot}>
                    Voltar ao login
                  </button>
                )}
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="kako-id">KAKO ID</Label>
                <Input
                  id="kako-id"
                  data-testid="reseller-login-id-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  placeholder="Seu KAKO ID"
                  value={kakoId}
                  onChange={(e) => setKakoId(e.target.value.replace(/\D/g, ''))}
                  required
                />
                <KakoIdentity kakoId={kakoId} tone="purple" testId="reseller-login-identity" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  data-testid="reseller-login-password-input"
                  type="password"
                  placeholder="Sua senha de afiliado"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {error && (
                <Alert variant="destructive" data-testid="reseller-login-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                data-testid="reseller-login-submit"
                className="w-full h-11 text-base font-bold bg-gradient-to-r from-purple-600 to-blue-600
                           shadow-lg shadow-purple-600/30 transition-transform hover:brightness-110
                           active:scale-[0.99]"
                disabled={loading}
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando...</> : 'Entrar'}
              </Button>

              <button type="button" className="text-xs text-gray-500 underline w-full text-center"
                data-testid="reseller-forgot-password-link"
                onClick={() => { setForgotMode(true); setError(''); }}>
                Esqueci minha senha
              </button>
            </form>
            )}
          </CardContent>
        </Card>

        <a href="/inscricao-afiliado" data-testid="reseller-signup-link"
           className="mt-4 flex items-center justify-center gap-1.5 rounded-xl border border-purple-300/30
                      bg-purple-500/15 px-4 py-3 text-sm font-semibold text-purple-100
                      transition-colors hover:bg-purple-500/25">
          Ainda não é afiliado? Faça sua inscrição
          <ArrowRight className="h-4 w-4" />
        </a>

        <div className="mt-4 text-center">
          <a href="/" className="text-sm text-purple-200/70 hover:text-white">
            ← Voltar para página inicial
          </a>
        </div>
      </div>

      <KakoAuthModal
        open={forgotModal} onOpenChange={setForgotModal}
        purpose="recuperar_senha_afiliado" initialKakoId={forgotId} lockKakoId
        title="Confirmar que a conta é sua"
        onVerified={(t) => { setForgotToken(t); setForgotModal(false); setError(''); }}
      />
    </div>
  );
};

export default ResellerLogin;
